import { useFormContext } from "react-hook-form";
import { PlusIcon } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildCustomFields } from "components/CustomFieldGroupDropdown";

import FormCheckbox from "../fields/FormCheckbox";
import FormChipList from "../fields/FormChipList";
import FormDatePicker from "../fields/FormDatePicker";
import FormInput from "../fields/FormInput";
import FormMultiSelect from "../fields/FormMultiSelect";
import FormSelect from "../fields/FormSelect";
import FormTextarea from "../fields/FormTextarea";
import RemoveButton from "../fields/RemoveButton";
import { emptyCustomFieldValue } from "components/BaseForm/customFields/helpers";

const TABLE_PLACEHOLDER =
  'Column1, Column2; Row1Value1, Row1Value2; "Name with, comma", Value';

const customFieldSelectOptions = config =>
  (config.field_options?.options ?? []).map(o => ({
    key: o.key ?? o.value,
    value: o.value,
    text: o.text ?? o.value,
  }));

const CustomFieldControl = ({ config }) => {
  const common = {
    name: `custom_fields.${config.label}`,
    label: config.label,
    required: config.required,
    id: `custom-${config.id}`,
  };
  switch (config.field_type) {
    case "numeric":
      return <FormInput {...common} type="number" />;
    case "boolean":
      return <FormCheckbox {...common} />;
    case "date":
      return <FormDatePicker {...common} />;
    case "list":
      return <FormChipList {...common} placeholder="Add item…" />;
    case "table":
      return (
        <FormTextarea {...common} rows={4} placeholder={TABLE_PLACEHOLDER} />
      );
    case "select":
      return config.field_options?.type === "multi" ? (
        <FormMultiSelect
          {...common}
          options={customFieldSelectOptions(config)}
        />
      ) : (
        <FormSelect
          {...common}
          options={customFieldSelectOptions(config)}
          clearable
        />
      );
    default:
      return <FormInput {...common} />;
  }
};

// Reproduces the legacy CustomFieldForm: required fields (and, on edit,
// already-valued ones) show by default; a group control reveals every field in
// a group at once, an "add field" control reveals a single field, and each
// non-required revealed field can be removed again. The values live in the form
// under `custom_fields`; removing a field empties its value so Save deletes it.
export const CustomFieldsSection = ({ selection, setSelection, groups }) => {
  const { setValue } = useFormContext();
  const revealed = selection.filter(c => c.required || c.selected);
  const available = selection.filter(c => !c.required && !c.selected);

  const revealField = id =>
    setSelection(prev =>
      prev.map(c => (c.id === id ? { ...c, selected: true } : c))
    );

  const removeField = config => {
    setValue(`custom_fields.${config.label}`, emptyCustomFieldValue(config), {
      shouldDirty: true,
    });
    setSelection(prev =>
      prev.map(c => (c.id === config.id ? { ...c, selected: false } : c))
    );
  };

  const revealGroup = groupId =>
    setSelection(prev =>
      buildCustomFields({
        customFieldGroups: groups,
        customFieldConfigs: prev,
        selectedCustomFieldGroupID: groupId,
      })
    );

  return (
    <Card>
      <CardHeader className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2">
        <CardTitle>Custom Fields</CardTitle>
        <div className="tw:flex tw:gap-2">
          {available.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                <PlusIcon data-icon="inline-start" /> Add Field
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {available.map(config => (
                  <DropdownMenuItem
                    key={config.id}
                    onClick={() => revealField(config.id)}
                  >
                    {config.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {groups.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                <PlusIcon data-icon="inline-start" /> Custom Field Groups
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {groups.map(group => (
                  <DropdownMenuItem
                    key={group.id}
                    onClick={() => revealGroup(group.id)}
                  >
                    {group.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {revealed.length === 0 ? (
          <p className="tw:text-sm tw:text-[var(--color-text-muted)]">
            No custom fields selected.
          </p>
        ) : (
          <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
            {revealed.map(config => (
              <div key={config.id} className="tw:flex tw:items-end tw:gap-2">
                <div className="tw:min-w-0 tw:flex-1">
                  <CustomFieldControl config={config} />
                </div>
                {!config.required && (
                  <RemoveButton
                    label={`Remove ${config.label}`}
                    onClick={() => removeField(config)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
